/*
 * Copyright (c) 2026 Centrica. All rights reserved.
 *
 * This source code is licensed under the license found in the
 * LICENSE file in the root directory of this source tree.
 * Date: 2026-09-10
 */

import { ChangeDetectionStrategy, Component, input } from '@angular/core';

/**
 * Banda informativa de una línea, con apariencia de consola.
 *
 * Modo claro: fondo blanco con borde azul primary y tipografía monoespaciada.
 * Modo oscuro: se integra con los tokens del tema del sistema.
 *
 * Los colores, borde, fondo, tipografía y tamaños se pueden sobrescribir
 * individualmente vía inputs. Si un input no se pasa, se usa el valor por
 * defecto de la variante.
 *
 * @example
 * ```html
 * <!-- Por defecto: fondo blanco, borde azul, texto gris oscuro -->
 * <app-info-banner message="Defina campos en la Grilla..." />
 *
 * <!-- Personalizado: fondo negro, borde verde, texto verde, mono -->
 * <app-info-banner
 *   [showPrompt]="true"
 *   backgroundColor="#0b1f17"
 *   borderColor="#1f7a4a"
 *   textColor="#4ade80"
 *   fontSize="13px"
 *   fontFamily="'JetBrains Mono', monospace"
 *   message="Defina campos en la Grilla..." />
 * ```
 */
@Component({
  selector: 'app-info-banner',
  standalone: true,
  templateUrl: './info-banner.component.html',
  styleUrl: './info-banner.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InfoBannerComponent {
  /** Texto que se pinta dentro de la banda. */
  message = input.required<string>();

  /** Variante visual (define los colores por defecto). */
  variant = input<'info' | 'success' | 'warning' | 'error'>('info');

  /** Muestra u oculta el prompt `>` al inicio. */
  showPrompt = input<boolean>(true);

  // ── Personalización ────────────────────────────────────────────────

  /** Fondo de la banda. Si no se pasa, se usa el de la variante. */
  backgroundColor = input<string | null>(null);

  /** Color del borde. Si no se pasa, se usa el de la variante. */
  borderColor = input<string | null>(null);

  /** Color del texto principal. Si no se pasa, se usa el de la variante. */
  textColor = input<string | null>(null);

  /** Color del prompt `>`. Si no se pasa, se usa el del texto. */
  promptColor = input<string | null>(null);

  /** Tamaño de la fuente (ej: `13px`, `0.875rem`). */
  fontSize = input<string | null>(null);

  /** Familia tipográfica completa. */
  fontFamily = input<string | null>(null);

  /** Peso de la fuente (100–900). */
  fontWeight = input<string | number | null>(null);

  /** Espaciado entre letras (ej: `0.01em`). */
  letterSpacing = input<string | null>(null);

  /** Padding interno (ej: `10px 14px`). */
  padding = input<string | null>(null);

  /** Radio de las esquinas (ej: `6px`, `12px`). */
  borderRadius = input<string | null>(null);
}
